import { BOGGLE_ACHIEVEMENT_DEFINITIONS } from '~/boggle/config/achievements/registry'
import { getBoggleBadgeDefinition } from '~/boggle/config/badges/registry'
import type { BoggleBadgeId, BoggleBadgeLevel } from '~/boggle/config/badges/types'
import type { BoggleRunSummaryRatingId } from '~/boggle/config/runSummaryScore'
import type { BoggleGameModeId } from '~/boggle/gameModes'
import { buildBoggleAchievementCareerStats } from '~/boggle/lib/achievements/careerStats'
import {
  evaluateBoggleAchievements,
  type BoggleAchievementEvaluationContext,
} from '~/boggle/lib/achievements/evaluateAchievements'
import {
  getBoggleChallengeBestScoreFromRuns,
  getBoggleStandardBestScoreFromRuns,
} from './bestScoreProjections'
import { createEmptyBoggleBadgeProgressProjection } from './badgeProjections'
import type { BoggleLocalProgressStore } from './localProgressStore'
import type {
  BoggleBadgeProgressProjection,
  BoggleCompletedRun,
  BoggleUnviewedAchievementTier,
  BoggleUnviewedBadge,
  BoggleUnviewedWord,
  BoggleWordOccurrence,
  BoggleWordProjectionSnapshot,
  BoggleWordStat,
  PlayerAchievementState,
} from './types'
import { BOGGLE_PLAYER_ACHIEVEMENT_STATE_VERSION } from './types'
import { buildBoggleBadgeProgressProjection } from './badgeProjections'
import { buildBoggleWordProjections } from './wordProjections'
import {
  computeBoggleUnviewedBadgesFromRun,
  computeBoggleUnviewedWordsFromRun,
  getBoggleCompletedRunDisplayWords,
  hasEarnedBoggleBadgeRecord,
  mergeBoggleUnviewedBadges,
} from './unviewedProgress'
import { notifyBoggleUnviewedChanged } from './unviewedNotifications'
import type { BoggleWordMetadataLexicon } from './wordMetadata'

export interface BoggleProgressRepository {
  acknowledgeUnviewedAchievementTiers(): Promise<readonly BoggleUnviewedAchievementTier[]>
  acknowledgeUnviewedBadges(): Promise<readonly BoggleUnviewedBadge[]>
  acknowledgeUnviewedWords(): Promise<readonly BoggleUnviewedWord[]>
  backfillAchievementProgress(): Promise<BoggleAchievementMergeResult>
  commitCompletedRun(run: BoggleCompletedRun): Promise<BoggleCompletedRunCommitResult>
  evaluateAndMergeAchievements(): Promise<BoggleAchievementMergeResult>
  getAchievementState(): Promise<PlayerAchievementState>
  getBadgeProgress(): Promise<BoggleBadgeProgressProjection>
  getChallengeBestScore(
    gameModeId: BoggleGameModeId,
    progressSeed: number | string,
  ): Promise<null | number>
  getCompletedRuns(): Promise<BoggleCompletedRun[]>
  getStandardBestScore(gameModeId: BoggleGameModeId): Promise<null | number>
  getUnviewedAchievementTiers(): Promise<BoggleUnviewedAchievementTier[]>
  getUnviewedBadges(): Promise<BoggleUnviewedBadge[]>
  getUnviewedWords(): Promise<BoggleUnviewedWord[]>
  getWordOccurrences(): Promise<BoggleWordOccurrence[]>
  getWordStats(): Promise<BoggleWordStat[]>
  rebuildBadgeProgressProjection(): Promise<BoggleBadgeProgressProjection>
  rebuildWordProjections(): Promise<BoggleWordProjectionSnapshot>
}

export type BoggleAchievementMergeResult = {
  backfilledTierIds: readonly string[]
  newlyRevealedRatingIds: readonly BoggleRunSummaryRatingId[]
  newlyUnlockedTierIds: readonly string[]
  state: PlayerAchievementState
}

export type BoggleCompletedRunProgressSummary = {
  completedRunCount: number
  completedRunIndex: number
  improvedBadgeCount: number
  newAchievementTierCount: number
  newBadgeCount: number
  newBadgeCountsByLevel: Record<BoggleBadgeLevel, number>
  newRatingRevealCount: number
  newUniqueWordCount: number
  totalAchievementTierCount: number
  totalBadgeCount: number
  totalBadgeCountsByLevel: Record<BoggleBadgeLevel, number>
  totalRevealedRatingCount: number
  totalUniqueWordCount: number
}

export type BoggleCompletedRunCommitResult =
  | { inserted: false }
  | ({
      inserted: true
      progressSummary: BoggleCompletedRunProgressSummary
    } & Pick<
      BoggleAchievementMergeResult,
      'newlyRevealedRatingIds' | 'newlyUnlockedTierIds'
    >)

const HIDDEN_RATING_IDS = [
  'diamond',
  'ace',
] as const satisfies readonly BoggleRunSummaryRatingId[]

const ACHIEVEMENT_TIER_ORDER = new Map(
  BOGGLE_ACHIEVEMENT_DEFINITIONS.flatMap(definition =>
    definition.tiers.map(tier => tier.id),
  ).map((tierId, index) => [tierId, index]),
)

const BOGGLE_BADGE_LEVELS = [
  'common',
  'impossible',
  'legendary',
  'rare',
  'uncommon',
] as const satisfies readonly BoggleBadgeLevel[]

function compareAchievementTierIds(left: string, right: string) {
  return (
    (ACHIEVEMENT_TIER_ORDER.get(left) ?? Number.MAX_SAFE_INTEGER) -
      (ACHIEVEMENT_TIER_ORDER.get(right) ?? Number.MAX_SAFE_INTEGER) ||
    left.localeCompare(right)
  )
}

function createEmptyPlayerAchievementState(): PlayerAchievementState {
  return {
    backfilledTierIds: [],
    revealedRatingIds: [],
    schemaVersion: BOGGLE_PLAYER_ACHIEVEMENT_STATE_VERSION,
    unlockedTierIds: [],
  }
}

function mergeUniqueSorted(existing: readonly string[], incoming: readonly string[]) {
  return [...new Set([...existing, ...incoming])].sort(compareAchievementTierIds)
}

function mergeRevealedRatings(
  existing: readonly BoggleRunSummaryRatingId[],
  context: BoggleAchievementEvaluationContext,
) {
  const merged = new Set(existing)
  for (const ratingId of HIDDEN_RATING_IDS) {
    if (context.careerStats.ratingIdsReached.includes(ratingId)) {
      merged.add(ratingId)
    }
  }
  return [...merged]
}

function createEmptyBadgeLevelCounts(): Record<BoggleBadgeLevel, number> {
  return Object.fromEntries(
    BOGGLE_BADGE_LEVELS.map(level => [level, 0]),
  ) as Record<BoggleBadgeLevel, number>
}

function incrementBadgeLevelCount(
  counts: Record<BoggleBadgeLevel, number>,
  badgeId: BoggleBadgeId,
) {
  const definition = getBoggleBadgeDefinition(badgeId)
  if (!definition) {
    return
  }
  counts[definition.level] += 1
}

function buildCompletedRunProgressSummary({
  achievementMerge,
  badgeProgress,
  completedRunCount,
  incomingBadges,
  incomingWordCount,
  totalUniqueWordCount,
}: {
  achievementMerge: BoggleAchievementMergeResult
  badgeProgress: BoggleBadgeProgressProjection
  completedRunCount: number
  incomingBadges: readonly BoggleUnviewedBadge[]
  incomingWordCount: number
  totalUniqueWordCount: number
}): BoggleCompletedRunProgressSummary {
  const newBadgeCountsByLevel = createEmptyBadgeLevelCounts()
  const totalBadgeCountsByLevel = createEmptyBadgeLevelCounts()
  let newBadgeCount = 0
  let improvedBadgeCount = 0
  let totalBadgeCount = 0

  for (const badge of incomingBadges) {
    if (badge.kind === 'new') {
      newBadgeCount += 1
      incrementBadgeLevelCount(newBadgeCountsByLevel, badge.badgeId)
    } else {
      improvedBadgeCount += 1
    }
  }

  for (const [badgeId, record] of Object.entries(badgeProgress.perBadge)) {
    if (!hasEarnedBoggleBadgeRecord(record)) {
      continue
    }
    totalBadgeCount += 1
    incrementBadgeLevelCount(totalBadgeCountsByLevel, badgeId as BoggleBadgeId)
  }

  return {
    completedRunCount,
    completedRunIndex: completedRunCount,
    improvedBadgeCount,
    newAchievementTierCount: achievementMerge.newlyUnlockedTierIds.length,
    newBadgeCount,
    newBadgeCountsByLevel,
    newRatingRevealCount: achievementMerge.newlyRevealedRatingIds.length,
    newUniqueWordCount: incomingWordCount,
    totalAchievementTierCount: achievementMerge.state.unlockedTierIds.length,
    totalBadgeCount,
    totalBadgeCountsByLevel,
    totalRevealedRatingCount: achievementMerge.state.revealedRatingIds.length,
    totalUniqueWordCount,
  }
}

export class DefaultBoggleProgressRepository implements BoggleProgressRepository {
  constructor(
    private readonly store: BoggleLocalProgressStore,
    private readonly metadataLexicon: BoggleWordMetadataLexicon,
  ) {}

  async acknowledgeUnviewedAchievementTiers() {
    const entries = await this.store.getUnviewedAchievementTiers()
    await this.store.clearUnviewedAchievementTiers()
    notifyBoggleUnviewedChanged()
    return entries
  }

  async acknowledgeUnviewedBadges() {
    const entries = await this.store.getUnviewedBadges()
    await this.store.clearUnviewedBadges()
    notifyBoggleUnviewedChanged()
    return entries
  }

  async acknowledgeUnviewedWords() {
    const entries = await this.store.getUnviewedWords()
    await this.store.clearUnviewedWords()
    notifyBoggleUnviewedChanged()
    return entries
  }

  async commitCompletedRun(run: BoggleCompletedRun) {
    const existing = await this.store.getCompletedRun(run.id)
    if (existing) {
      return { inserted: false as const }
    }

    if (!(await this.store.getPlayerAchievementState())) {
      await this.backfillAchievementProgress()
    }

    const priorBadgeProgress =
      (await this.store.getBadgeProgress()) ?? createEmptyBoggleBadgeProgressProjection()
    const priorWordStats = await this.store.getWordStats()
    const incomingBadges = computeBoggleUnviewedBadgesFromRun({
      earnedBadges: run.earnedBadges,
      priorBadgeProgress,
      runRatingId: run.ratingId,
    })
    const incomingWords = computeBoggleUnviewedWordsFromRun({
      displayWords: getBoggleCompletedRunDisplayWords(run),
      priorWordStats,
    })

    await this.store.putCompletedRun(run)
    const badgeProgress = await this.rebuildBadgeProgressProjection()
    const wordProjections = await this.rebuildWordProjections()
    const achievementMerge = await this.evaluateAndMergeAchievements()
    const incomingAchievementTiers = achievementMerge.newlyUnlockedTierIds.map(
      tierId => ({
        tierId,
      }),
    )

    if (incomingBadges.length > 0) {
      const merged = mergeBoggleUnviewedBadges(
        await this.store.getUnviewedBadges(),
        incomingBadges,
      )
      await this.store.replaceUnviewedBadges(merged)
    }

    if (incomingWords.length > 0) {
      const merged = new Map(
        (await this.store.getUnviewedWords()).map(entry => [entry.normalizedWord, entry]),
      )
      for (const entry of incomingWords) {
        merged.set(entry.normalizedWord, entry)
      }
      await this.store.replaceUnviewedWords([...merged.values()])
    }

    if (incomingAchievementTiers.length > 0) {
      const merged = new Map(
        (await this.store.getUnviewedAchievementTiers()).map(entry => [
          entry.tierId,
          entry,
        ]),
      )
      for (const entry of incomingAchievementTiers) {
        merged.set(entry.tierId, entry)
      }
      await this.store.replaceUnviewedAchievementTiers([...merged.values()])
    }

    if (
      incomingBadges.length > 0 ||
      incomingWords.length > 0 ||
      incomingAchievementTiers.length > 0
    ) {
      notifyBoggleUnviewedChanged()
    }

    return {
      inserted: true,
      newlyRevealedRatingIds: achievementMerge.newlyRevealedRatingIds,
      newlyUnlockedTierIds: achievementMerge.newlyUnlockedTierIds,
      progressSummary: buildCompletedRunProgressSummary({
        achievementMerge,
        badgeProgress,
        completedRunCount: (await this.store.getCompletedRuns()).length,
        incomingBadges,
        incomingWordCount: incomingWords.length,
        totalUniqueWordCount: wordProjections.wordStats.length,
      }),
    }
  }

  async getCompletedRuns() {
    return this.store.getCompletedRuns()
  }

  async getStandardBestScore(gameModeId: BoggleGameModeId) {
    return getBoggleStandardBestScoreFromRuns(
      await this.store.getCompletedRuns(),
      gameModeId,
    )
  }

  async getChallengeBestScore(gameModeId: BoggleGameModeId, progressSeed: number | string) {
    return getBoggleChallengeBestScoreFromRuns(
      await this.store.getCompletedRuns(),
      gameModeId,
      progressSeed,
    )
  }

  async getAchievementState() {
    const state = await this.store.getPlayerAchievementState()
    return state ?? (await this.backfillAchievementProgress()).state
  }

  async getBadgeProgress() {
    return (await this.store.getBadgeProgress()) ?? this.rebuildBadgeProgressProjection()
  }

  async getUnviewedBadges() {
    return this.store.getUnviewedBadges()
  }

  async getUnviewedAchievementTiers() {
    return this.store.getUnviewedAchievementTiers()
  }

  async getUnviewedWords() {
    return this.store.getUnviewedWords()
  }

  async getWordOccurrences() {
    return this.store.getWordOccurrences()
  }

  async getWordStats() {
    return this.store.getWordStats()
  }

  async rebuildBadgeProgressProjection() {
    const projection = buildBoggleBadgeProgressProjection(
      await this.store.getCompletedRuns(),
    )
    await this.store.replaceBadgeProgressProjection(projection)
    return projection
  }

  async rebuildWordProjections() {
    const projections = buildBoggleWordProjections(
      await this.store.getCompletedRuns(),
      this.metadataLexicon,
    )
    await this.store.replaceWordProjections(projections)
    return projections
  }

  async evaluateAndMergeAchievements() {
    return this.mergeAchievements({ backfill: false })
  }

  async backfillAchievementProgress() {
    await this.rebuildBadgeProgressProjection()
    await this.rebuildWordProjections()
    return this.mergeAchievements({ backfill: true })
  }

  private async buildAchievementContext(): Promise<BoggleAchievementEvaluationContext> {
    const completedRuns = await this.store.getCompletedRuns()
    const wordOccurrences = await this.store.getWordOccurrences()
    const wordProjections =
      wordOccurrences.length > 0 || completedRuns.length === 0
        ? { wordOccurrences }
        : await this.rebuildWordProjections()
    const badgeProgress = await this.getBadgeProgress()

    return {
      badgeProgress,
      careerStats: buildBoggleAchievementCareerStats({
        completedRuns,
        wordProjections,
      }),
    }
  }

  private async mergeAchievements({
    backfill,
  }: {
    backfill: boolean
  }): Promise<BoggleAchievementMergeResult> {
    const context = await this.buildAchievementContext()
    const existing =
      (await this.store.getPlayerAchievementState()) ??
      createEmptyPlayerAchievementState()
    const completedTierIds = evaluateBoggleAchievements(
      BOGGLE_ACHIEVEMENT_DEFINITIONS,
      context,
    )
    const previousUnlocked = new Set(existing.unlockedTierIds)
    const existingBackfilled = new Set(existing.backfilledTierIds)
    const unlockedNow = completedTierIds.filter(
      tierId => !previousUnlocked.has(tierId) && !existingBackfilled.has(tierId),
    )
    const backfilledTierIds = backfill ? unlockedNow : []
    const revealedRatingIds = mergeRevealedRatings(existing.revealedRatingIds, context)
    const previousRevealedRatings = new Set(existing.revealedRatingIds)
    const newlyRevealedRatingIds = revealedRatingIds.filter(
      ratingId => !previousRevealedRatings.has(ratingId),
    )

    const state: PlayerAchievementState = {
      backfilledTierIds: mergeUniqueSorted(existing.backfilledTierIds, backfilledTierIds),
      revealedRatingIds,
      schemaVersion: BOGGLE_PLAYER_ACHIEVEMENT_STATE_VERSION,
      unlockedTierIds: mergeUniqueSorted(existing.unlockedTierIds, completedTierIds),
    }

    await this.store.putPlayerAchievementState(state)

    return {
      backfilledTierIds,
      newlyRevealedRatingIds: backfill ? [] : newlyRevealedRatingIds,
      newlyUnlockedTierIds: backfill ? [] : unlockedNow,
      state,
    }
  }
}
